import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useSpareRequisitions } from '../../spares/requisitions/useData';
import { useSupplierQuotations } from '../../spares/supplier-quotations/useData';
import { useSparePurchaseOrders } from '../../spares/spare-purchase-orders/useData';
import { useSpareDeliveries } from '../../spares/spare-deliveries/useData';
import { useSpareInvoices } from '../../spares/spare-invoices/useData';
import { StatCounterGrid, type StatCounterItem } from './shared';

const SparesPipelineCounters: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { colors } = useTheme();
  const { total: requisitionsTotal, isLoading: reqLoading } = useSpareRequisitions(undefined, 1, 1);
  const { total: quotationsTotal, isLoading: quoLoading } = useSupplierQuotations(undefined, 1, 1);
  const { total: ordersTotal, isLoading: poLoading } = useSparePurchaseOrders(undefined, 1, 1);
  const { total: deliveriesTotal, isLoading: delLoading } = useSpareDeliveries(undefined, 1, 1);
  const { total: invoicesTotal, isLoading: invLoading } = useSpareInvoices(undefined, 1, 1);

  const isLoading = reqLoading || quoLoading || poLoading || delLoading || invLoading;
  const counters: StatCounterItem[] = [
    {
      key: 'req',
      label: 'Spare Requisition',
      value: requisitionsTotal ?? 0,
      href: '/spares/requisitions',
      accent: colors.primary,
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h10M9 9h10M9 13h10M5 5h.01M5 9h.01M5 13h.01M4 19h16" />
        </svg>
      ),
    },
    {
      key: 'quo',
      label: 'Quotations',
      value: quotationsTotal ?? 0,
      href: '/spares/supplier-quotations',
      accent: colors.accent,
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8M8 11h8M8 15h5M7 3h10a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V5a2 2 0 012-2z" />
        </svg>
      ),
    },
    {
      key: 'po',
      label: 'Purchase Orders',
      value: ordersTotal ?? 0,
      href: '/spares/spare-purchase-orders',
      accent: colors.primary,
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7l9-4 9 4-9 4-9-4zm0 5l9 4 9-4m-18 5l9 4 9-4" />
        </svg>
      ),
    },
    {
      key: 'dlv',
      label: 'Deliveries',
      value: deliveriesTotal ?? 0,
      href: '/spares/spare-deliveries',
      accent: 'bg-emerald-500',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17h6m-9 0a2 2 0 11-4 0 2 2 0 014 0zm16 0a2 2 0 11-4 0 2 2 0 014 0zM5 17V9h9v8M14 11h4l2 3v3h-6v-6z" />
        </svg>
      ),
    },
    {
      key: 'inv',
      label: 'Invoices',
      value: invoicesTotal ?? 0,
      href: '/spares/spare-invoices',
      accent: 'bg-[#6b8fb8]',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4h10a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 012-2zm2 4h6m-6 4h6m-6 4h4" />
        </svg>
      ),
    },
  ];

  return (
    <StatCounterGrid
      title="Spares · Pipeline"
      counters={counters}
      isLoading={isLoading}
      embedded={embedded}
    />
  );
};

export default SparesPipelineCounters;
